// A trial on the loaded data (modules' migration/trial.ts), on the migration's overview: where it stands,
// and the owner's two actions, each confirmed by typing the company's name. Starting pauses the import
// and lets the office use the full app on what's loaded; ending throws everything changed away (shown
// first, counted per table) and the import resumes. The worker does the copying, so this follows the
// trial's state every few seconds while it starts or ends.

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { Panel, errorText, useQuery } from '../books/ui.tsx';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { setTrial } from '../shared/trial.ts';
import { holds } from './model.ts';

interface TrialRow {
  id: string;
  state: 'starting' | 'on' | 'ending' | 'ended' | 'failed';
  requestedAt: string;
  snapshotAt: string | null;
  endRequestedAt: string | null;
  endedAt: string | null;
  step: string | null;
  error: string | null;
  outcome: { filesDeleted: number; filesLeft: number; seconds: number | null } | null;
}

interface Preview {
  since: string;
  tables: { table: string; label: string; added: number; changed: number; removed: number }[];
  filesAdded: number;
}

const OPEN = ['starting', 'on', 'ending'];

const stamp = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );

export function TrialPanel({
  held,
  company,
  timeZone,
  toast,
}: {
  held: readonly string[];
  company: string;
  timeZone: string;
  toast: (m: string) => void;
}) {
  const q = useQuery<{ trial: TrialRow | null }>('migration.trial', {});
  const [sheet, setSheet] = useState<'start' | 'end' | null>(null);
  const t = q.data?.trial ?? null;
  const busy = t?.state === 'starting' || t?.state === 'ending';
  // the office's notice follows what this place reads; while the worker copies, read again
  const loaded = q.data !== null;
  useEffect(() => {
    if (!loaded) return;
    const s = t?.state;
    setTrial(
      s === 'starting' || s === 'on' || s === 'ending' ? { state: s, since: t?.snapshotAt ?? null } : null,
    );
  }, [loaded, t]);
  const reload = q.reload;
  useEffect(() => {
    if (!busy) return;
    const id = setInterval(() => void reload(), 5000);
    return () => clearInterval(id);
  }, [busy, reload]);
  const owner = holds(held, ['migration.signOff']);
  const open = !!t && OPEN.includes(t.state);
  return (
    <Panel title="Trial on the loaded data">
      {!open && (
        <p className="muted">
          Try the full app on the records loaded so far. The import pauses, nothing goes to customers or
          suppliers, no money moves, and everything changed is thrown away when the trial ends.
        </p>
      )}
      {t?.state === 'starting' && (
        <p role="status">Starting: the snapshot is being taken{t.step ? ` (${t.step})` : ''}.</p>
      )}
      {t?.state === 'on' && (
        <p role="status">
          <b>On</b> since {t.snapshotAt ? stamp(t.snapshotAt, timeZone) : 'now'}. The import is paused.
        </p>
      )}
      {t?.state === 'ending' && (
        <p role="status">Ending: the company is being put back{t.step ? ` (${t.step})` : ''}.</p>
      )}
      {t?.state === 'ended' && t.endedAt && (
        <p className="muted small">
          The last trial ended {stamp(t.endedAt, timeZone)}
          {t.outcome
            ? `: ${t.outcome.filesDeleted} file${t.outcome.filesDeleted === 1 ? '' : 's'} deleted`
            : ''}
          {t.outcome?.filesLeft ? `, ${t.outcome.filesLeft} left in storage` : ''}.
        </p>
      )}
      {t?.error && (
        <div className="notice warn">
          <p>
            {t.state === 'failed' ? 'The trial didn’t start: ' : 'Last try: '}
            {t.error}
          </p>
        </div>
      )}
      {q.error && <p className="muted small">{q.error}</p>}
      {owner && !open && (
        <button type="button" className="btn" onClick={() => setSheet('start')}>
          Start a trial
        </button>
      )}
      {owner && t?.state === 'on' && (
        <button type="button" className="btn outline" onClick={() => setSheet('end')}>
          End the trial
        </button>
      )}
      {sheet === 'start' && (
        <StartTrial
          company={company}
          onClose={() => setSheet(null)}
          onDone={() => {
            toast('The trial is starting. The office is unavailable for a minute or two.');
            setSheet(null);
            void q.reload();
          }}
        />
      )}
      {sheet === 'end' && (
        <EndTrial
          company={company}
          onClose={() => setSheet(null)}
          onDone={() => {
            toast('The trial is ending. Its changes are being thrown away.');
            setSheet(null);
            void q.reload();
          }}
        />
      )}
    </Panel>
  );
}

function useTyped(action: string, onDone: () => void) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call(action, { typedName: name.trim() });
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return { name, setName, busy, error, go };
}

function StartTrial({
  company,
  onClose,
  onDone,
}: {
  company: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const f = useTyped('migration.startTrial', onDone);
  return (
    <MoneySheet
      title="Start a trial"
      outcome={
        <>
          The import pauses and the office can use everything on the records loaded so far. Nothing goes to
          customers or suppliers and no money moves. Everything changed is thrown away when the trial ends.
          The office is unavailable for a minute or two while the snapshot is taken.
        </>
      }
      confirm="Start the trial"
      ready={f.name.trim().length > 1}
      busy={f.busy}
      error={f.error}
      onConfirm={() => void f.go()}
      onClose={onClose}
    >
      <label>
        Type {company} to confirm
        <input
          className="field"
          autoComplete="off"
          value={f.name}
          onChange={(e) => f.setName(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}

function EndTrial({
  company,
  onClose,
  onDone,
}: {
  company: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const f = useTyped('migration.endTrial', onDone);
  const p = useQuery<Preview>('migration.trialPreview', {});
  const lines = p.data?.tables.filter((l) => l.table !== 'files') ?? [];
  return (
    <MoneySheet
      title="End the trial"
      outcome={
        <>
          Everything changed since the trial started is thrown away, and the import resumes where it stopped.
          The office is unavailable for a few seconds at the switch.
        </>
      }
      confirm="Throw the changes away"
      ready={f.name.trim().length > 1 && !!p.data}
      busy={f.busy}
      error={f.error ?? p.error}
      onConfirm={() => void f.go()}
      onClose={onClose}
    >
      {p.data ? (
        lines.length || p.data.filesAdded ? (
          <div className="table-wrap">
            <table className="grid-table" aria-label="What is thrown away">
              <thead>
                <tr>
                  <th>Records</th>
                  <th className="num">Added</th>
                  <th className="num">Changed</th>
                  <th className="num">Removed</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.table}>
                    <td>{l.label}</td>
                    <td className="num">{l.added.toLocaleString('en-CA')}</td>
                    <td className="num">{l.changed.toLocaleString('en-CA')}</td>
                    <td className="num">{l.removed.toLocaleString('en-CA')}</td>
                  </tr>
                ))}
                <tr>
                  <td>Files (deleted from storage)</td>
                  <td className="num">{p.data.filesAdded.toLocaleString('en-CA')}</td>
                  <td className="num">–</td>
                  <td className="num">–</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">Nothing in the main records has changed since the trial started.</p>
        )
      ) : (
        <p className="muted">Counting what would be thrown away…</p>
      )}
      <label>
        Type {company} to confirm
        <input
          className="field"
          autoComplete="off"
          value={f.name}
          onChange={(e) => f.setName(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}
